export interface TourStop {
  id: number;
  slug: string;
  title: string;
  subtitle: string;
  category: 'indoor' | 'outdoor' | 'artifact' | 'architecture';
  categoryLabel: string;
  location: string;
  description: string;
  historicalContext: string;
  audioPrompt?: string;
  imageUrl: string;
  lookFor: string[];
}

export const TOUR_STOPS: TourStop[] = [
  {
    id: 1,
    slug: 'historic-schoolhouse',
    title: 'The 1912 Wooden Schoolhouse',
    subtitle: 'Oldest Remaining Wooden Schoolhouse in St. Tammany Parish',
    category: 'architecture',
    categoryLabel: 'Historic Architecture',
    location: 'Museum Exterior & Main Hall',
    description: 'Constructed over a century ago in 1912, this two-room wooden building once served generations of Lacombe children. Built from native heart pine and cypress, the schoolhouse retains its original architecture, high ceilings designed for Louisiana breezes, and large double-hung windows.',
    historicalContext: 'During early rural Louisiana history, schools were the social and civic heartbeats of the township. In 1976, local citizens mobilized to rescue the schoolhouse from demolition, dedicating it as the permanent home of the Bayou Lacombe Museum.',
    imageUrl: 'https://images.ctfassets.net/f6ou4743hwzs/2xaL3IYyS5iU8bcXoWAOdX/9d1784950839614bbbcaa593c23ee090/whyblm.jpg',
    lookFor: [
      'Original native longleaf heart pine flooring',
      'High transom windows for natural cooling before AC',
      'The original bell tower mount and entrance facade'
    ]
  },
  {
    id: 2,
    slug: 'choctaw-heritage',
    title: 'Choctaw Heritage & River Culture',
    subtitle: 'Indigenous Ingenuity & Centuries of Lifeways',
    category: 'artifact',
    categoryLabel: 'Indigenous Artifacts',
    location: 'East Gallery Room',
    description: 'Long before French traders traversed Lake Pontchartrain, Choctaw communities established thriving settlements along Bayou Lacombe. They navigated the waterways in hollowed pirogues, gathered shellfish, and crafted intricate river cane baskets colored with native root and walnut dyes.',
    historicalContext: 'The indigenous peoples passed on indispensable survival wisdom to early colonial settlers, including brick clay extraction methods and cypress wood durability. This room preserves authentic Choctaw blowguns, river cane weaving, and prehistoric shell mound artifacts.',
    imageUrl: 'https://images.ctfassets.net/f6ou4743hwzs/7e1JcZ26BUogr7h3Sp9Xtk/f8c556293fc104792561f33ecb9e0c2b/blmbasket.jpeg',
    lookFor: [
      'Intricate Choctaw river cane baskets and natural dye patterns',
      'Hunting blowguns and darts tipped with thistle down',
      'Ancient shell tools recovered from bayou middens'
    ]
  },
  {
    id: 3,
    slug: 'chahta-ima-sanctuary',
    title: 'Father Adrien Rouquette ("Chahta-Ima")',
    subtitle: 'Poet-Priest and Defender of the Choctaw',
    category: 'indoor',
    categoryLabel: 'Historical Biography',
    location: 'Memorial Alcove',
    description: 'Father Adrien-Emmanuel Rouquette (1813–1887) spent decades living among the Choctaw along Bayou Lacombe. Ordained as a Catholic priest, he chose the solitude of the pine barrens over city life, building small rustic chapels (including the Chapel of the Transfiguration) nestled in the woods.',
    historicalContext: 'Rouquette is commemorated as Louisiana’s first native poet-priest, composing verses in French and Choctaw. His writings captured the vanishing wilderness and fiercely defended the rights of the indigenous population during an era of displacement.',
    imageUrl: 'https://images.ctfassets.net/f6ou4743hwzs/5sUzt4o4BuNjfeqyZ3f8EF/b9fcdbc8848bce9fa798d56d9bf8a2c1/Blmpere.jpg',
    lookFor: [
      'Original portraiture and rare published manuscripts',
      'Facsimiles of Father Rouquette’s handwritten poetry',
      'Artifacts from his rustic wilderness hermitage chapels'
    ]
  },
  {
    id: 4,
    slug: 'brickmaking-and-lumber',
    title: 'The Industries that Built New Orleans',
    subtitle: 'Brick Kilns, Schooners & Timber',
    category: 'indoor',
    categoryLabel: 'Early Industry',
    location: 'West Gallery Room',
    description: 'To protect New Orleans from catastrophic fires and subtropical termites in the 1700s and 1800s, builders looked north across Lake Pontchartrain. Lacombe’s iron-rich clay was molded into sun-dried and kiln-fired bricks, while massive virgin cypress was milled for lumber.',
    historicalContext: 'Fleet schooners sailed out of Bayou Lacombe carrying hundreds of thousands of handmade bricks across the lake to construct New Orleans landmarks, plantations, and parish infrastructure.',
    imageUrl: 'https://images.ctfassets.net/f6ou4743hwzs/5sylZnr8AMOP4BLPf1e0UJ/40e07d9ae0f4a3df6c4113ef578c0db4/Davis_Tools.jpg',
    lookFor: [
      'Handmade colonial bricks stamped with historic maker marks',
      'Two-man crosscut timber saws and turpentine collection tools',
      'Photographs of the schooners docked at Bayou Lacombe wharves'
    ]
  },
  {
    id: 5,
    slug: 'la-toussaint-tradition',
    title: 'La Toussaint & Creole Cemetery Customs',
    subtitle: 'Blessing the Graves by Candlelight',
    category: 'indoor',
    categoryLabel: 'Folk Traditions',
    location: 'Heritage Exhibit Room',
    description: 'Lacombe’s celebration of La Toussaint (All Saints’ Day) is one of the most remarkable living cultural traditions in the United States. On November 1st, families gather at rural cemeteries to whitewash above-ground tombs, decorate them with flowers, and ignite hundreds of beeswax candles as dusk descends.',
    historicalContext: 'A syncretic blend of Choctaw remembrance of ancestors, French Catholic All Saints liturgy, and African-Creole family reverence, this centuries-old tradition continues actively in Lacombe today.',
    imageUrl: 'https://images.ctfassets.net/f6ou4743hwzs/25J49fl7nXv1vbAQQ489m6/18bfc00b509745060c5024415a379b5f/AllSaints_2019.webp',
    lookFor: [
      'Oral history recordings from local Lacombe elders',
      'Photographs of Saint Henry Cemetery illuminated at twilight',
      'Handmade candles and historical burial markers'
    ]
  },
  {
    id: 6,
    slug: 'native-plant-gardens',
    title: 'Heritage Gardens & Native Botanical Trail',
    subtitle: 'Cypress, Longleaf Pines & Pollinators',
    category: 'outdoor',
    categoryLabel: 'Outdoor Trail',
    location: 'Museum Grounds & Courtyard',
    description: 'Step outdoors onto the museum grounds to explore the native flora of coastal Louisiana. The heritage gardens showcase indigenous medicinal and edible plants utilized by the Choctaw, alongside towering live oaks, longleaf pines, and native pollinator flowers.',
    historicalContext: 'Preserving the natural ecology of the bayou is fundamental to honoring the people who thrived here. The gardens serve as an outdoor classroom for regional biodiversity and conservation.',
    imageUrl: 'https://images.ctfassets.net/f6ou4743hwzs/4Ai2kjxhOLh3jxvqUlylAx/10b6b1b57942dc16e9a40aba842e74b0/MVIMG_20191022_184543.jpg',
    lookFor: [
      'Medicinal plants identified with botanical placards',
      'The majestic canopy of heritage live oaks',
      'Outdoor educational pavilion and picnic area'
    ]
  }
];
